import { DateOnly } from '@sevenrooms/timepiece'
import { VStack, NotificationBanner, Flex } from '@sevenrooms/ui-kit/layout'
import { getRelativeFormattedDate } from '../../../components/ReservationDayPickerForm/getRelativeFormattedDate'
import { ColumnTimeSlots } from './ColumnTimeSlots'
import { RowTimeSlots } from './RowTimeSlots'
import { TimeSlotsSkeleton } from './TimeSlotsSkeleton'
import type { ReservationTimeSlotVariant } from '../../../components/ReservationTimeslot'
import type { AvailabilityTimeWithUpSellCost } from '../../../hooks'
import type { ReactNode } from 'react'

interface TimeSlotsProps {
  direction?: 'row' | 'column'
  descriptionType?: 'short' | 'full'
  filteredTimeslots?: AvailabilityTimeWithUpSellCost[]
  requestOrPriorityAlertAvailability?: ReactNode
  noAvailabilityMessage?: ReactNode
  currencyCode: string
  startDate?: string
  timeSlotVariant?: ReservationTimeSlotVariant
  timeSlotsDisplayCount?: number
  showTimeslotTime?: boolean
  isLoading?: boolean
  onTimeSlotClick?: (slot: AvailabilityTimeWithUpSellCost) => void
}

export function TimeSlots({
  direction = 'column',
  filteredTimeslots,
  requestOrPriorityAlertAvailability,
  noAvailabilityMessage,
  isLoading,
  onTimeSlotClick,
  startDate,
  timeSlotVariant = 'primary',
  timeSlotsDisplayCount,
  showTimeslotTime,
  currencyCode,
  descriptionType,
}: TimeSlotsProps) {
  const startDateJs = DateOnly.fromSafe(startDate)?.toJsDate()
  const formattedStartDate = showTimeslotTime && startDateJs ? getRelativeFormattedDate({ date: startDateJs }) : undefined

  if (!!filteredTimeslots?.length || isLoading) {
    return (
      <VStack spacing="m">
        {isLoading && (
          <>
            {formattedStartDate && <Flex>{formattedStartDate}</Flex>}
            <TimeSlotsSkeleton />
          </>
        )}
        {!isLoading &&
          !!filteredTimeslots &&
          (direction === 'column' ? (
            <ColumnTimeSlots
              timeslots={filteredTimeslots}
              descriptionType={descriptionType}
              onTimeSlotClick={onTimeSlotClick}
              timeSlotVariant={timeSlotVariant}
              timeSlotsDisplayCount={timeSlotsDisplayCount}
              startDate={formattedStartDate}
              currencyCode={currencyCode}
              requestOrPriorityAlertAvailability={requestOrPriorityAlertAvailability}
            />
          ) : (
            <RowTimeSlots
              timeslots={filteredTimeslots}
              descriptionType={descriptionType}
              onTimeSlotClick={onTimeSlotClick}
              timeSlotVariant={timeSlotVariant}
              startDate={formattedStartDate}
              currencyCode={currencyCode}
              requestOrPriorityAlertAvailability={requestOrPriorityAlertAvailability}
            />
          ))}
      </VStack>
    )
  }

  if (noAvailabilityMessage) {
    return <NotificationBanner type="info" data-test="sr-room-card-no-availability-text" showIcon content={noAvailabilityMessage} />
  }

  return null
}
